import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Upload, 
  Download, 
  Refresh, 
  FileUpload, 
  MonetizationOn,
  Business,
  CheckCircle,
  ErrorOutline,
  AccountBalance,
  Edit as EditIcon,
  CallSplit as SplitIcon,
  FolderOpen
} from '@mui/icons-material';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { idsSeleccionados, SELECCION_VACIA } from '../../utils/gridSelection';

// Module scope so its identity is stable: a fresh array each render would rebuild the grid's
// state-manager options every time.
const ORDEN_POR_DEFECTO: GridSortModel = [{ field: 'Lote', sort: 'desc' }];
import { ConceptSelector } from '../Common/ConceptSelector';
import { SplitInvoiceModal } from '../Common/SplitInvoiceModal';
import { ProjectStatusBadge } from '../Common/ProjectStatusBadge';
import { ProjectSelector } from '../Common/ProjectSelector';
import { CostReportProjectSelector } from '../Common/CostReportProjectSelector';
import { makeDocuwareColumn } from '../Common/docuwareColumn';
import { useDocuwareLink } from '../../hooks/useDocuwareLink';
import { ProjectSyncCard } from '../Common/ProjectSyncCard';
import { Project } from '../../types';

interface Factura {
  id: string; // DataGrid ID (for MUI DataGrid)
  ID: number; // Database ID (unique primary key)
  NIT: string;
  'PROVEEDOR': string;
  'TIPO DOC.': string;
  'CONCEPTO': string;
  'FECHA': string;
  'No. DOC.': string;
  // The supplier's own invoice number. Distinct from 'No. DOC.' above, which is SINCO's
  // internal consecutive -- this is the one that matches the paper document. Often '',
  // because the lot extractors write an empty string when SINCO reports no value.
  'Factura'?: string;
  'V/R COBRO': number;
  'Concepto Aprobacion': string;
  // A number, not a string: [Lote] is `int` in the database and arrives as a JS number. The
  // declaration used to say string, which matters because it invites someone to "fix" the
  // sort with a string comparator -- the default one already orders 316 above 99 correctly.
  'Lote': number;
  'Tipo Lote': string;
  'CP'?: string;
  'ID Docuware'?: string | null; // DocuWare document ID, null when not yet resolved
  // The project's fiducia and its code/description for each concepto. Empty string
  // when the fiducia is unresolved or the concepto is not in the equivalencias
  // catalog -- the report is produced either way.
  'Fiducia'?: string | null;
  'CUIF'?: string;
  'DESC.'?: string;
  // Where the concept currently shown came from: 'Docuware', 'Adpro - Control del proyecto',
  // or 'Manual - <name>' when a person set it with the pencil in this grid. Read-only here --
  // the server writes it in the same statement as the concept, so the two cannot disagree.
  // Null for rows classified before the column existed and for rows with no concept.
  //
  // Splitting an invoice and the bulk spreadsheet upload deliberately do NOT stamp it, so a
  // row touched only by those keeps whatever source it had.
  'Fuente Concepto'?: string | null;
  'Subcapitulo'?: string | null;
  // The budget item's code as SINCO wrote it, check digit included ('070050100-9'). Same
  // lifecycle as 'Subcapitulo': written with the concept, cleared when a person edits it.
  'Codigo Item'?: string | null;
  // A copy of 'Concepto Aprobacion' (V12), written by every path that writes it. CUIF and DESC.
  // are looked up with it, so they do not move when the concept changes methodology.
  'Concepto Fiducia'?: string | null;
  // The chapter heading of the concept (V13), indirect costs included. 'Concepto Aprobacion'
  // shows the sub-chapter for chapter concepts, so this is where the chapter still shows.
  'Capitulo'?: string | null;
}

// [Capitulo] for a concept a person chose: the concept itself when it is a chapter heading,
// null otherwise. The same rule the API applies in the same write, so the optimistic cell and
// the stored one agree (api/app/utils/concept_operations.py, _SQL_CAPITULO_DE).
const capituloDeConcepto = (concepto: string | null | undefined): string | null =>
  concepto && /^\s*cap[ií]tulo\s+\d/i.test(concepto) ? concepto : null;

interface ReporteFacturasResultProps {
  initialData?: Factura[];
  syncCsrfToken?: string;
  /** DocuWare server base URL, injected via data-react-props */
  docuwareBaseUrl?: string;
  /** Sucursal -> DocuWare file cabinet GUID, injected via data-react-props */
  docuwareCabinets?: Record<string, string>;
}

interface StatisticsData {
  totalProyectos: number;
  saldoDisponible: number;
  saldoCanje: number;
  saldoTotal: number;
}

// EditableCell component for inline editing
const EditableCell: React.FC<{
  params: GridRenderCellParams;
  onEdit: (newValue: string) => Promise<void>;
  pendingOperations: Set<string>;
  optimisticUpdates: Map<string, Factura>;
  setAlert: (value: { type: 'success' | 'error' | 'info'; message: string }) => void;
  onSplit: (row: Factura) => void;
  onEditingChange: (id: string, editing: boolean) => void;
}> = ({ params, onEdit, pendingOperations, optimisticUpdates, setAlert, onSplit, onEditingChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(params.value || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => () => onEditingChange(params.row.id, false), [params.row.id, onEditingChange]);

  // Check if this row has pending operations
  const hasPendingOperation = Array.from(pendingOperations).some(op => op.includes(params.row.id));
  const hasOptimisticUpdate = optimisticUpdates.has(params.row.id);

  const handleStartEdit = () => {
    setIsEditing(true);
    onEditingChange(params.row.id, true);
    setTempValue(params.value || '');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    onEditingChange(params.row.id, false);
    setTempValue(params.value || '');
  };

  const handleConfirmEdit = async () => {
    if (tempValue === params.value) {
      setIsEditing(false);
      onEditingChange(params.row.id, false);
      return;
    }

    setIsSubmitting(true);
    try {
      await onEdit(tempValue);
      setIsEditing(false);
      onEditingChange(params.row.id, false);
      setAlert({ type: 'success', message: 'Concepto actualizado exitosamente' });
    } catch (error) {
      console.error('Error updating concept:', error);
      setAlert({ type: 'error', message: 'Error al actualizar el concepto' });
      setTempValue(params.value || ''); // Revert to original value
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
        <ConceptSelector
          value={tempValue}
          onChange={setTempValue}
          disabled={isSubmitting}
          size="small"
          fullWidth
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmEdit}
            disabled={isSubmitting}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            ✓
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCancelEdit}
            disabled={isSubmitting}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            ✕
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {hasPendingOperation && (
          <CircularProgress size={16} />
        )}
        <Tooltip title="Editar concepto">
          <IconButton
            size="small"
            onClick={handleStartEdit}
            disabled={hasPendingOperation}
            sx={{
              color: 'blue',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 255, 0.1)',
                color: 'blue'
              },
              '&:disabled': {
                color: 'rgba(0, 0, 255, 0.3)'
              }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Subdividir factura">
          <IconButton
            size="small"
            onClick={() => onSplit(params.row)}
            disabled={hasPendingOperation}
            sx={{
              color: 'blue',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 255, 0.1)',
                color: 'blue'
              },
              '&:disabled': {
                color: 'rgba(0, 0, 255, 0.3)'
              }
            }}
          >
            <SplitIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          opacity: hasOptimisticUpdate ? 0.7 : 1,
          fontStyle: hasOptimisticUpdate ? 'italic' : 'normal'
        }}
      >
        {params.value || ''}
      </Typography>
    </Box>
  );
};

export const ReporteFacturasResult: React.FC<ReporteFacturasResultProps> = ({
  initialData = [],
  docuwareBaseUrl,
  docuwareCabinets,
  syncCsrfToken = ''
}) => {
  // State management
  const [facturas, setFacturas] = useState<Factura[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const selectedProjectRef = useRef('');
  const reportRequest = useRef(0);
  const [currentProjectInfo, setCurrentProjectInfo] = useState<Project | null>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  
  // Edit and split functionality state
  const [mafpModalOpen, setMafpModalOpen] = useState(false);
  const [lotesExcluidos, setLotesExcluidos] = useState('');

  // Checkbox selection, for reclassifying several invoices at once.
  const [modeloSeleccion, setModeloSeleccion] = useState<GridRowSelectionModel>(SELECCION_VACIA);
  const [dialogoLoteAbierto, setDialogoLoteAbierto] = useState(false);
  const [conceptoLote, setConceptoLote] = useState('');
  const [guardandoLote, setGuardandoLote] = useState(false);

  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const onEditingChange = useCallback((rowId: string, editing: boolean) => {
    setEditingCell(previous => editing ? { rowId, field: 'Concepto Aprobacion' }
      : previous?.rowId === rowId ? null : previous);
  }, []);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [selectedRecordForSplit, setSelectedRecordForSplit] = useState<Factura | null>(null);
  
  // State management for operations
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(new Set());
  const [optimisticUpdates, setOptimisticUpdates] = useState<Map<string, Factura>>(new Map());
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());

  // Load data from URL params instead of sessionStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const proyecto = urlParams.get('proyecto');
    
    if (proyecto) {
      selectedProjectRef.current = proyecto;
      setSelectedProject(proyecto);
      loadReporteData(proyecto);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // There used to be an auto-refresh here: whenever no operation was pending and 30 seconds
  // had passed, it refetched the whole report two seconds later. That is what made the table
  // reload by itself after an edit -- losing scroll position, page and any in-flight cell --
  // and it existed because a local edit left CUIF and DESC. stale, since both are derived
  // server-side from the concept.
  //
  // It is gone, and the two things it was papering over are fixed instead: handleEditConcept
  // now patches those cells from the server after a successful save, and
  // rollbackOptimisticUpdate actually restores the previous values when a save fails. Do not
  // reintroduce a periodic refetch to fix a stale cell -- patch the cell.
  //
  // Data still refreshes on the paths where it must: choosing a project, the explicit refresh
  // control, and splitting an invoice, which creates rows this component cannot invent.

  const loadProjectInfo = async (projectName: string) => {
    try {
      // Load project information with active status
      const response = await fetch('/api/v1/configuracion_proyectos');
      if (!response.ok) throw new Error('Error al cargar información del proyecto');
      
      const data = await response.json();
      const projectsArray = JSON.parse(data.proyectos);
      
      // Find the current project
      const project = projectsArray.find((p: any) => p['Nombre Proyecto'] === projectName);
      if (project && selectedProjectRef.current === projectName) {
        const projectInfo: Project = {
          'Codigo Centro Costos': project['Codigo Centro Costos'],
          'Nombre Proyecto': project['Nombre Proyecto'],
          'Tipo Centro Costos': project['Tipo Centro Costos'],
          'Sucursal': project['Sucursal'] || undefined, // drives the DocuWare cabinet lookup
          'Ultima Actualizacion Sistema': project['Ultima Actualizacion Sistema'],
          'Ultima Actualizacion Usuario': project['Ultima Actualizacion Usuario'],
          active: project.active !== undefined ? project.active : true
        };
        setCurrentProjectInfo(projectInfo);
      }
    } catch (error) {
      console.error('Error loading project info:', error);
      // Don't show error for project info loading as it's not critical
    }
  };

  const loadReporteData = async (proyecto: string) => {
    if (!proyecto || selectedProjectRef.current !== proyecto) return;
    const requestVersion = ++reportRequest.current;
    setIsLoading(true);
    try {
      // Load project information first
      await loadProjectInfo(proyecto);
      
      const response = await fetch('/obtener-reporte-costos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proyecto })
      });
      
      if (!response.ok) throw new Error('Error al cargar reporte');
      const data = await response.json();
      
      if (selectedProjectRef.current !== proyecto || requestVersion !== reportRequest.current) return;
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `factura_${index}_${item['PROVEEDOR'] || index}`, // MUI DataGrid ID
        ID: Number(item.ID), // Database ID (preserve original)
        'V/R COBRO': Number(item['V/R COBRO'] || 0),
      }));
      
      setFacturas(transformedData);
      setModeloSeleccion(SELECCION_VACIA);
      setAlert({ type: 'success', message: `Reporte cargado para: ${proyecto}` });
    } catch (error) {
      console.error('Error loading report:', error);
      if (selectedProjectRef.current === proyecto && requestVersion === reportRequest.current) setAlert({ type: 'error', message: 'Error al cargar reporte' });
    } finally {
      if (selectedProjectRef.current === proyecto && requestVersion === reportRequest.current) setIsLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    try {
      // Handle different date formats that might come from the backend
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if can't parse
      
      // Format as dd/mm/yyyy (Colombian format)
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original if formatting fails
    }
  };

  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date(0);
    try {
      return new Date(dateString);
    } catch (error) {
      return new Date(0);
    }
  };

  const handleDownloadExcel = () => {
    if (!selectedProject) {
      setAlert({ type: 'error', message: 'Se requiere seleccionar un proyecto para descargar el reporte' });
      return;
    }
    const downloadUrl = `/download-reporte-facturas-excel?proyecto=${encodeURIComponent(selectedProject)}`;
    window.open(downloadUrl, '_blank');
  };

  const handleDownloadMAFPExcel = () => {
    if (!selectedProject) {
      setAlert({ type: 'error', message: 'Se requiere seleccionar un proyecto para descargar el reporte MAFP' });
      return;
    }
    setLotesExcluidos('');
    setMafpModalOpen(true);
  };

  const handleConfirmMAFPDownload = () => {
    const params = new URLSearchParams({ proyecto: selectedProject });
    if (lotesExcluidos.trim()) {
      params.append('lotes_excluidos', lotesExcluidos.trim());
    }
    window.open(`/download-reporte-facturas-mafp-excel?${params.toString()}`, '_blank');
    setMafpModalOpen(false);
  };

  const handleRefresh = async (proyecto: string) => {
    setIsManualRefreshing(true);
    try {
      await refreshDataGrid(true);
    } catch (error) {
      console.error('Error refreshing report:', error);
      setAlert({ type: 'error', message: 'Error al actualizar el reporte' });
    } finally {
      setIsManualRefreshing(false);
    }
  };

  const handleProjectChange = async (newProject: string) => {
    if (newProject && newProject !== selectedProject) {
      selectedProjectRef.current = newProject;
      setCurrentProjectInfo(null);
      setSelectedProject(newProject);
      await loadReporteData(newProject);
      setShowProjectSelector(false);
    }
  };

  // A project belongs to exactly one Sucursal, so the DocuWare cabinet is a single
  // value per report. Branches with no mapped cabinet resolve to undefined and the
  // "Ver PDF" button is hidden rather than pointing at the wrong cabinet.
  const docuwareCabinet = currentProjectInfo?.Sucursal
    ? docuwareCabinets?.[currentProjectInfo.Sucursal]
    : undefined;
  const getDocuwareLink = useDocuwareLink(docuwareCabinet, docuwareBaseUrl);

  useEffect(() => {
    if (currentProjectInfo?.Sucursal && !docuwareCabinet) {
      console.warn(
        `No hay gabinete DocuWare configurado para la sucursal '${currentProjectInfo.Sucursal}'. ` +
        'No se mostrarán botones "Ver PDF".'
      );
    }
  }, [currentProjectInfo?.Sucursal, docuwareCabinet]);

  // Title for the fiducia description column. The report covers one project, so the
  // fiducia is the same on every row; falls back to a bare 'DESC.' when the project
  // has no fiducia resolved yet, keeping the column present either way.
  const tituloDescFiducia = (() => {
    const fiducia = (facturas[0]?.['Fiducia'] ?? '').toString().trim();
    return fiducia ? `DESC. ${fiducia}` : 'DESC.';
  })();

  // Column definitions. Deliberately rebuilt on every render: the renderCell closures
  // below capture EditableCell/handleEditConcept, which must see current state.
  const columns: GridColDef[] = [
    makeDocuwareColumn(getDocuwareLink),
    {
      field: 'ID',
      headerName: 'ID Interno',
      width: 100,
      type: 'number',
      filterable: false,
      sortable: true,
    },
    {
      field: 'NIT',
      headerName: 'NIT',
      width: 120,
      filterable: true,
      sortable: true,
    },
    {
      field: 'PROVEEDOR',
      headerName: 'PROVEEDOR',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'TIPO DOC.',
      headerName: 'TIPO DOC.',
      width: 100,
      filterable: true,
      sortable: true,
    },
    {
      field: 'CONCEPTO',
      headerName: 'CONCEPTO',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'FECHA',
      headerName: 'FECHA',
      width: 120,
      type: 'date',
      filterable: true,
      sortable: true,
      valueGetter: (value) => value && new Date(value),
      renderCell: (params) => {
        // Access original row data for display formatting
        const originalDate = params.row.FECHA;
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {originalDate ? formatDate(originalDate) : ''}
          </Typography>
        );
      },
    },
    {
      field: 'No. DOC.',
      headerName: 'No. DOC.',
      width: 100,
      filterable: true,
      sortable: true,
    },
    {
      // Sits beside 'No. DOC.' because the two are read together: the internal consecutive
      // and the number printed on the supplier's document. In the Excel export it goes LAST
      // instead, which is not an inconsistency -- see COLUMNAS_REPORTE_FACTURAS in
      // io_functions.py, where any position before the tenth repoints the pivot tables.
      //
      // No `type: 'number'`, and that is not a detail: most invoice numbers carry a
      // supplier prefix ('FE434343', 'TERA3154') -- roughly three in four of the non-empty
      // ones are not numeric at all. Declaring the column numeric would make every one of
      // those sort and filter as an empty cell. It is nvarchar(50) in the schema, and the
      // extractors write '' when SINCO reports no value, so blank means unknown.
      field: 'Factura',
      headerName: 'FACTURA',
      width: 130,
      filterable: true,
      sortable: true,
    },
    {
      field: 'V/R COBRO',
      headerName: 'V/R COBRO',
      width: 150,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      valueFormatter: (params) => formatCurrency(params.value || 0),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {formatCurrency(params.value || 0)}
        </Typography>
      ),
    },
    {
      field: 'Concepto Aprobacion',
      headerName: 'Concepto Aprobacion',
      width: 280,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <EditableCell
          params={params}
          pendingOperations={pendingOperations}
          optimisticUpdates={optimisticUpdates}
          setAlert={setAlert}
          onSplit={handleOpenSplitModal}
          onEditingChange={onEditingChange}
          onEdit={(newValue) => handleEditConcept(params.row.id, newValue)}
        />
      ),
    },
    {
      // Right before the sub-chapter it contains. Read-only, like the sub-chapter: it is the
      // chapter the concept chain resolved, and editing the concept is how it changes.
      field: 'Capitulo',
      headerName: 'CAPITULO',
      width: 250,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      // Immediately to the right of the concept it belongs to, because the two are read
      // together: the chapter says WHERE in the budget, the sub-chapter says WHICH part of the
      // project. The Excel export puts it last instead -- that mismatch is deliberate and
      // explained in COLUMNAS_REPORTE_FACTURAS (the pivot cache addresses fields by index).
      //
      // Read-only on purpose. It is derived from the budget item the concept chain resolved,
      // so a person editing it by hand would be asserting something SINCO's budget contradicts,
      // and nothing downstream would notice.
      field: 'Subcapitulo',
      headerName: 'Subcapitulo',
      width: 200,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      // Right after the sub-chapter, and read-only for the same reason: it is the budget item
      // the concept chain resolved, not something a person chooses. Last in the Excel export,
      // like 'Subcapitulo', because the pivot cache addresses fields by index.
      field: 'Codigo Item',
      headerName: 'Codigo Item',
      width: 130,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      field: 'Lote',
      headerName: 'Lote',
      width: 60,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Tipo Lote',
      headerName: 'Tipo Lote',
      width: 120,
      filterable: true,
      sortable: true,
    },
    {
      // Sits next to 'Tipo Lote' here and LAST in the Excel export, and the two orders are
      // meant to differ -- do not "fix" one to match the other. COLUMNAS_REPORTE_FACTURAS in
      // WebUI/web/utils/io_functions.py is referenced by POSITION by the workbook's pivot-table
      // cache, so moving a column there repoints a pivot at the wrong field and raises no
      // error at all. Column order in this grid has no such consequence.
      //
      // Read-only: no `editable`, so the grid refuses inline edits. The value is written
      // server-side in the same statement as the concept -- including 'Manual - <name>' when a
      // person edits with the pencil -- so it cannot disagree with the concept beside it.
      field: 'Fuente Concepto',
      headerName: 'Fuente Concepto',
      width: 200,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      // Right before CUIF, because CUIF and DESC. are derived from it. Read-only: it is written
      // with the concept, so editing the concept is how it changes.
      field: 'Concepto Fiducia',
      headerName: 'Concepto Fiducia',
      width: 250,
      filterable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{params.value || ''}</span>
      ),
    },
    {
      field: 'CUIF',
      headerName: 'CUIF',
      width: 110,
      filterable: true,
      sortable: true,
    },
    {
      // Titled with the project's fiducia while the field key stays fixed, so the
      // column contract shared with the Excel export does not vary per project.
      field: 'DESC.',
      headerName: tituloDescFiducia,
      width: 240,
      filterable: true,
      sortable: true,
    }
  ];

  const closeAlert = () => setAlert(null);

  // State management utilities
  const addPendingOperation = (operationId: string) => {
    setPendingOperations(prev => new Set(prev).add(operationId));
  };

  const removePendingOperation = (operationId: string) => {
    setPendingOperations(prev => {
      const newSet = new Set(prev);
      newSet.delete(operationId);
      return newSet;
    });
  };

  const addOptimisticUpdate = (rowId: string, updatedData: Partial<Factura>) => {
    setOptimisticUpdates(prev => {
      const newMap = new Map(prev);
      const existingData = newMap.get(rowId) || facturas.find(f => f.id === rowId);
      if (existingData) {
        newMap.set(rowId, { ...existingData, ...updatedData });
      }
      return newMap;
    });
  };

  const removeOptimisticUpdate = (rowId: string) => {
    setOptimisticUpdates(prev => {
      const newMap = new Map(prev);
      newMap.delete(rowId);
      return newMap;
    });
  };

  const applyOptimisticUpdate = (rowId: string, updatedData: Partial<Factura>) => {
    setFacturas(prev => prev.map(f => 
      f.id === rowId ? { ...f, ...updatedData } : f
    ));
  };

  // Takes the values to restore rather than looking them up. It used to read the row out of
  // `facturas`, which applyOptimisticUpdate had already overwritten -- so it "restored" the new
  // value and a failed save left the wrong text on screen. The 30s auto-refresh used to hide
  // that by reloading everything; now that the refresh is gone, this has to actually work.
  const rollbackOptimisticUpdate = (rowId: string, valoresOriginales: Partial<Factura>) => {
    setFacturas(prev => prev.map(f =>
      f.id === rowId ? { ...f, ...valoresOriginales } : f
    ));
    removeOptimisticUpdate(rowId);
  };

  // Data refresh with state synchronization
  const refreshDataGrid = async (showSuccessMessage: boolean = true) => {
    if (!selectedProject) return false;
    const proyecto = selectedProject;
    const requestVersion = ++reportRequest.current;
    
    try {
      setIsLoading(true);
      
      // Refresh project info first
      await loadProjectInfo(selectedProject);
      
      const response = await fetch('/obtener-reporte-costos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proyecto: selectedProject })
      });
      
      if (!response.ok) throw new Error('Error al cargar reporte');
      const data = await response.json();
      
      if (selectedProjectRef.current !== proyecto || requestVersion !== reportRequest.current) return false;
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `factura_${index}_${item['PROVEEDOR'] || index}`, // MUI DataGrid ID
        ID: Number(item.ID), // Database ID (preserve original)
        'V/R COBRO': Number(item['V/R COBRO'] || 0),
      }));
      
      setFacturas(transformedData);
      setLastRefreshTime(new Date());
      
      // Clear any pending operations and optimistic updates
      setPendingOperations(new Set());
      setOptimisticUpdates(new Map());
      // And the selection, which is not optional. Row ids embed the array index
      // (`factura_${index}_...`), so a selection kept across a reload would silently re-attach
      // to whichever invoices now occupy those positions. Clearing is the only safe move until
      // the ids are keyed on the database [ID].
      setModeloSeleccion(SELECCION_VACIA);
      
      if (showSuccessMessage) {
        setAlert({ type: 'success', message: 'Datos actualizados exitosamente' });
      }
      return true;
    } catch (error) {
      console.error('Error refreshing data:', error);
      if (selectedProjectRef.current === proyecto && requestVersion === reportRequest.current) setAlert({ type: 'error', message: 'Error al actualizar los datos' });
      return false;
    } finally {
      if (selectedProjectRef.current === proyecto && requestVersion === reportRequest.current) setIsLoading(false);
    }
  };

  // SplitButton component
  const SplitButton: React.FC<{ rowData: Factura }> = ({ rowData }) => (
    <Tooltip title="Subdividir factura">
      <IconButton
        size="small"
        onClick={() => handleOpenSplitModal(rowData)}
        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
      >
        <SplitIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Handler for editing concept with optimistic updates
  /**
   * Resolve CUIF and DESC. for one concept, or null if they cannot be resolved.
   *
   * Never throws and never surfaces an error: the concept was already saved by the time this
   * runs, and failing to refresh two derived cells must not report the edit as failed. Same
   * contract the server-side helper follows -- on any failure of an external source, leave the
   * columns as they are and let the report through.
   */
  const resolverColumnasFiducia = async (
    proyecto: string,
    concepto: string
  ): Promise<{ CUIF: string; 'DESC.': string } | null> => {
    try {
      const respuesta = await fetch('/resolver-columnas-fiducia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proyecto, concepto }),
      });
      if (!respuesta.ok) return null;
      const datos = await respuesta.json();
      if (!datos?.success) return null;
      return { CUIF: datos.CUIF ?? '', 'DESC.': datos['DESC.'] ?? '' };
    } catch {
      return null;
    }
  };

  // --- Reclassifying several invoices at once -------------------------------------------

  // Must match MAX_FILAS_LOTE in api/app/utils/concept_operations.py. Checked here too so an
  // oversized selection is refused before a round trip, with a message naming the number.
  const MAX_FILAS_LOTE = 500;

  // Resolved from the selection model against the rows currently on screen. The `idsVisibles`
  // argument is what makes the header "select all" work: that produces an *exclude* model,
  // where the selection can only be derived by subtracting from what is visible.
  const idsFilasSeleccionadas = idsSeleccionados(
    modeloSeleccion, facturas.map(f => f.id)
  );
  const facturasSeleccionadas = facturas.filter(
    f => idsFilasSeleccionadas.includes(f.id)
  );
  const excedeTope = facturasSeleccionadas.length > MAX_FILAS_LOTE;

  const limpiarSeleccion = () => setModeloSeleccion(SELECCION_VACIA);

  const abrirDialogoLote = () => {
    setConceptoLote('');
    setDialogoLoteAbierto(true);
  };

  /**
   * Apply one concept to every selected invoice.
   *
   * Deliberately does NOT loop `handleEditConcept`: that function fires a `setAlert` on each
   * success and failure, so N rows would raise N alerts. This path makes a single request and
   * reports once.
   */
  const aplicarConceptoALote = async () => {
    if (!conceptoLote || facturasSeleccionadas.length === 0) return;

    setGuardandoLote(true);
    // Snapshot for rollback, taken before anything is applied optimistically.
    const originales = new Map(facturasSeleccionadas.map(f => [f.id, {
      'Concepto Aprobacion': f['Concepto Aprobacion'],
      'Concepto Fiducia': f['Concepto Fiducia'],
      'Capitulo': f['Capitulo'],
      'Fuente Concepto': f['Fuente Concepto'],
      'Subcapitulo': f['Subcapitulo'],
      'Codigo Item': f['Codigo Item'],
      'CUIF': f['CUIF'],
      'DESC.': f['DESC.'],
    }]));

    try {
      const respuesta = await fetch('/api/v1/facturas/concepto-aprobacion/lote', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: facturasSeleccionadas.map(f => ({
            record_id: f.ID, new_concept: conceptoLote,
          })),
        }),
      });

      const datos = await respuesta.json().catch(() => null);
      if (!respuesta.ok || !datos?.success) {
        throw new Error(datos?.detail || datos?.message || 'No se pudo aplicar el concepto');
      }

      // One call, not one per row: every selected invoice gets the same concept, so CUIF and
      // DESC. resolve to the same pair for all of them.
      const fiducia = await resolverColumnasFiducia(selectedProject, conceptoLote);

      const idsEditados = new Set(facturasSeleccionadas.map(f => f.id));
      setFacturas(prev => prev.map(f => idsEditados.has(f.id)
        ? {
            ...f,
            'Concepto Aprobacion': conceptoLote,
            // Written server-side in the same statement as the concept (V12).
            'Concepto Fiducia': conceptoLote,
            'Capitulo': capituloDeConcepto(conceptoLote),
            // Taken from the server's answer, never written as a literal here: the string
            // lives in api/app/utils/actor.py and must not exist in a second place. Same rule
            // the pencil follows, which reads it out of `updated_record`.
            ...(datos.fuente_aplicada ? { 'Fuente Concepto': datos.fuente_aplicada } : {}),
            // Cleared server-side with the concept -- same pair rule the pencil follows.
            'Subcapitulo': null,
            'Codigo Item': null,
            ...(fiducia ? { 'CUIF': fiducia.CUIF, 'DESC.': fiducia['DESC.'] } : {}),
          }
        : f));

      const faltantes: number[] = datos.not_found || [];
      setAlert({
        type: faltantes.length ? 'info' : 'success',
        message: faltantes.length
          ? `${datos.updated_count} factura(s) actualizada(s). ${faltantes.length} ya no existían.`
          : `${datos.updated_count} factura(s) actualizada(s).`,
      });

      setDialogoLoteAbierto(false);
      limpiarSeleccion();
    } catch (error: any) {
      // Nothing was written when the server refuses, so the screen is put back as it was.
      setFacturas(prev => prev.map(f => originales.has(f.id)
        ? { ...f, ...originales.get(f.id)! }
        : f));
      setAlert({ type: 'error', message: error?.message || 'No se pudo aplicar el concepto' });
    } finally {
      setGuardandoLote(false);
    }
  };

  const handleEditConcept = async (rowId: string, newValue: string): Promise<void> => {
    const factura = facturas.find(f => f.id === rowId);
    if (!factura) {
      throw new Error('Factura no encontrada');
    }

    const operationId = `edit_${rowId}_${Date.now()}`;
    // Snapshot every cell this edit can move, taken BEFORE the optimistic update: CUIF and
    // DESC. are derived from the concept, and Fuente Concepto is rewritten by the server.
    const valoresOriginales: Partial<Factura> = {
      'Concepto Aprobacion': factura['Concepto Aprobacion'],
      'Concepto Fiducia': factura['Concepto Fiducia'],
      'Capitulo': factura['Capitulo'],
      'Fuente Concepto': factura['Fuente Concepto'],
      'Subcapitulo': factura['Subcapitulo'],
      'Codigo Item': factura['Codigo Item'],
      'CUIF': factura['CUIF'],
      'DESC.': factura['DESC.'],
    };

    try {
      // Add pending operation
      addPendingOperation(operationId);
      
      // Apply optimistic update immediately
      // Concepto Fiducia moves with it: the server writes both in one statement (V12).
      const parcheConcepto = {
        'Concepto Aprobacion': newValue,
        'Concepto Fiducia': newValue,
        'Capitulo': capituloDeConcepto(newValue),
      };
      addOptimisticUpdate(rowId, parcheConcepto);
      applyOptimisticUpdate(rowId, parcheConcepto);

      // Use API service function
      const { updateConceptApprovalWithFeedback } = await import('../../utils/apiService');
      
      const response = await updateConceptApprovalWithFeedback({
        record_id: factura.ID,
        new_concept: newValue,
        proyecto: selectedProject
      }, {
        onSuccess: (message) => {
          setAlert({ type: 'success', message });
        },
        onError: (error) => {
          setAlert({ type: 'error', message: error });
        }
      });

      if (response && response.success) {
        // Patch the two things the edit changes beyond the concept itself, so the row is
        // correct without refetching the table. This is what replaces the auto-refresh that
        // used to repair these cells 30 seconds later.
        const parche: Partial<Factura> = {};

        // The source comes from the row the server just wrote, never from a literal here --
        // the string lives in api/app/utils/actor.py and should not be spelled out twice.
        const fuenteEscrita = (response as any)?.updated_record?.['Fuente Concepto'];
        if (fuenteEscrita !== undefined) {
          parche['Fuente Concepto'] = fuenteEscrita;
        }

        // The pair rule reaches the UI too: the server clears [Subcapitulo] whenever the
        // concept changes, because a person picks a chapter and not a budget item. Without
        // this the cell would keep showing the sub-chapter the SYNC derived for the concept
        // the person just replaced -- stale, and wrong in a way that looks authoritative.
        parche['Subcapitulo'] = null;
        parche['Codigo Item'] = null;
        // And [Capitulo] (V13) is the chosen concept when it is a heading, which the server
        // wrote in the same statement.
        parche['Capitulo'] = capituloDeConcepto(newValue);

        // CUIF and DESC. are derived from the concept against the equivalencias catalog, and
        // the browser does not hold that catalog -- /conceptos/equivalencias returns no CUIF.
        // So the server resolves them, reusing the same function the full report uses.
        const fiducia = await resolverColumnasFiducia(selectedProject, newValue);
        if (fiducia) {
          parche['CUIF'] = fiducia.CUIF;
          parche['DESC.'] = fiducia['DESC.'];
        }

        if (Object.keys(parche).length > 0) {
          applyOptimisticUpdate(rowId, parche);
        }

        // Confirm the optimistic update - no need to reload DataGrid
        removeOptimisticUpdate(rowId);
      } else {
        // Rollback optimistic update on failure
        rollbackOptimisticUpdate(rowId, valoresOriginales);
        throw new Error(response?.message || 'Error al actualizar el concepto');
      }
    } catch (error) {
      // Rollback optimistic update on error
      rollbackOptimisticUpdate(rowId, valoresOriginales);
      throw error;
    } finally {
      // Remove pending operation
      removePendingOperation(operationId);
    }
  };

  // Modal state management
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    isSubmitting: boolean;
    hasUnsavedChanges: boolean;
  }>({
    isOpen: false,
    isSubmitting: false,
    hasUnsavedChanges: false
  });

  // Handler for opening split modal
  const handleOpenSplitModal = (record: Factura) => {
    setSelectedRecordForSplit(record);
    setModalState({
      isOpen: true,
      isSubmitting: false,
      hasUnsavedChanges: false
    });
    setSplitModalOpen(true);
  };

  // Handler for closing split modal
  const handleCloseSplitModal = (forceClose: boolean = false) => {
    // Check for unsaved changes only if not forcing close
    if (!forceClose && modalState.hasUnsavedChanges && !modalState.isSubmitting) {
      const confirmClose = window.confirm(
        '¿Está seguro de que desea cerrar? Se perderán los cambios no guardados.'
      );
      if (!confirmClose) return;
    }
    
    setSplitModalOpen(false);
    setSelectedRecordForSplit(null);
    setModalState({
      isOpen: false,
      isSubmitting: false,
      hasUnsavedChanges: false
    });
  };

  // Handler for modal state changes
  const handleModalStateChange = (updates: Partial<typeof modalState>) => {
    setModalState(prev => ({ ...prev, ...updates }));
  };

  // Handler for confirming split with state management
  const handleConfirmSplit = async (splitData: any): Promise<void> => {
    if (!selectedRecordForSplit) {
      throw new Error('No se encontró el registro seleccionado');
    }

    const operationId = `split_${selectedRecordForSplit.id}_${Date.now()}`;

    try {
      // Add pending operation
      addPendingOperation(operationId);

      // Use API service function
      const { splitInvoiceRecordWithFeedback } = await import('../../utils/apiService');
      
      // Map camelCase field names to snake_case for API compatibility
      // The backend expects ALL splits (including original with remaining amount) 
      // with the original split as the first element
      const originalSplit = splitData.splits.find((s: any) => s.isOriginal);
      const newSplits = splitData.splits.filter((s: any) => !s.isOriginal);
      
      if (!originalSplit) {
        throw new Error('No se encontró el registro original en los splits');
      }
      
      const mappedSplits = [
        // Original split first (will update the original record)
        {
          concepto_aprobacion: originalSplit.conceptoAprobacion,
          vr_cobro: originalSplit.vrCobro
        },
        // Then all new splits (will create new records)
        ...newSplits.map((split: any) => ({
          concepto_aprobacion: split.conceptoAprobacion,
          vr_cobro: split.vrCobro
        }))
      ];

      const response = await splitInvoiceRecordWithFeedback({
        original_record: selectedRecordForSplit,
        splits: mappedSplits,
        proyecto: selectedProject
      }, {
        onSuccess: (message) => {
          setAlert({ 
            type: 'success', 
            message: `${message}. Factura subdividida exitosamente.` 
          });
        },
        onError: (error) => {
          setAlert({ type: 'error', message: error });
        }
      });

      if (response && response.success) {
        // Close modal first (force close without confirmation since operation was successful)
        handleCloseSplitModal(true);
        
        // Refresh data to show new records
        await refreshDataGrid(false);
        
        // Show success message with details
        setAlert({ 
          type: 'success', 
          message: `Factura subdividida exitosamente. Se crearon ${response.created_records?.length || 0} nuevos registros.` 
        });
      } else {
        throw new Error(response?.message || 'Error al subdividir la factura');
      }
    } catch (error) {
      throw error;
    } finally {
      // Remove pending operation
      removePendingOperation(operationId);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={closeAlert}
          sx={{ mb: 2 }}
          icon={alert.type === 'success' ? <CheckCircle /> : <ErrorOutline />}
        >
          {alert.message}
        </Alert>
      )}

      {/* Pending Operations Indicator */}
      {pendingOperations.size > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          icon={<CircularProgress size={20} />}
        >
          Procesando {pendingOperations.size} operación{pendingOperations.size > 1 ? 'es' : ''}...
        </Alert>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={2}>
              <MonetizationOn color="inherit" />
              <Box>
                <Typography variant="h5" component="div">
                  Reporte de Costos - Consolidado Facturas por Lotes
                </Typography>
                {currentProjectInfo && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                    <FolderOpen fontSize="small" sx={{ opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {currentProjectInfo['Nombre Proyecto']}
                    </Typography>
                    <ProjectStatusBadge
                      project={currentProjectInfo}
                      size="small"
                      showText={true}
                      showIcon={true}
                      sx={{
                        backgroundColor: currentProjectInfo.active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)',
                        color: currentProjectInfo.active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                        '& .MuiChip-icon': {
                          color: currentProjectInfo.active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<FolderOpen />}
                onClick={() => setShowProjectSelector(true)}
                size="small"
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              >
                Cambiar Proyecto
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadMAFPExcel}
                size="small"
                sx={{ color: 'primary.main', borderColor: 'primary.main' }}
              >
                Excel para MAFP
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadExcel}
                size="small"
              >
                Descargar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={isManualRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={() => handleRefresh(selectedProject)}
                size="small"
                disabled={isManualRefreshing}
              >
                {isManualRefreshing ? 'Actualizando...' : 'Actualizar vista'}
              </Button>
            </Stack>
          }
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-action': { 
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }
            }
          }}
        />
        <CardContent>
          {currentProjectInfo && currentProjectInfo['Nombre Proyecto'] === selectedProject && (
            <ProjectSyncCard
              key={currentProjectInfo['Codigo Centro Costos']}
              codigo={Number(currentProjectInfo['Codigo Centro Costos'])}
              csrfToken={syncCsrfToken}
              canRefresh={!editingCell && !splitModalOpen && !dialogoLoteAbierto
                && !guardandoLote && pendingOperations.size === 0 && !isLoading}
              onRefresh={() => refreshDataGrid(false)}
            />
          )}
          {/* Project Selection Prompt */}
          {!selectedProject && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => setShowProjectSelector(true)}
                  startIcon={<FolderOpen />}
                >
                  Seleccionar Proyecto
                </Button>
              }
            >
              <Typography variant="body2">
                Seleccione un proyecto para ver el reporte de costos y facturas.
              </Typography>
            </Alert>
          )}

          {/* Inactive Project Warning */}
          {currentProjectInfo && !currentProjectInfo.active && (
            <Alert 
              severity="warning" 
              sx={{ mb: 2 }}
              icon={<ErrorOutline />}
            >
              <Typography variant="body2">
                <strong>Proyecto Inactivo:</strong> Este proyecto está marcado como inactivo. 
                Los datos mostrados son históricos y el proyecto no participa en sincronizaciones actuales.
              </Typography>
            </Alert>
          )}

          {/* Bulk action bar. **Always rendered**, never mounted on selection.
              Appearing on the first tick pushed the table down, and a layout shift there is
              not cosmetic: somebody dragging the horizontal scrollbar at the bottom gets a
              checkbox slid under the cursor and ticks an invoice they never chose. Reserving
              the space permanently costs one row of screen and removes the hazard entirely.
              The border stays a fixed colour for the same reason -- an outline that comes and
              goes is the same flicker, even when it moves nothing. */}
          <Paper
            variant="outlined"
            sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
          >
            {facturasSeleccionadas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Marca facturas para editar su concepto en lote.
              </Typography>
            ) : (
              <Chip
                color={excedeTope ? 'error' : 'primary'}
                label={`${facturasSeleccionadas.length} seleccionada${facturasSeleccionadas.length === 1 ? '' : 's'}`}
                size="small"
              />
            )}
            {excedeTope && (
              <Typography variant="body2" color="error">
                Máximo {MAX_FILAS_LOTE} facturas por edición. Reduce la selección o filtra
                primero.
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              onClick={limpiarSeleccion}
              disabled={facturasSeleccionadas.length === 0}
            >
              Limpiar
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              disabled={excedeTope || facturasSeleccionadas.length === 0}
              onClick={abrirDialogoLote}
            >
              Editar concepto
            </Button>
          </Paper>

          {/* Data Grid */}
          <Box sx={{ mb: 2 }}>
            <TreasuryDataGrid
              rows={facturas}
              columns={columns}
              loading={isLoading}
              keepRowsWhileLoading
              showToolbar={true}
              showExport={true}
              showRefresh={true}
              title="Reporte de Costos - Consolidado Facturas por Lotes"
              tableId="reporte-conceptos-facturas"
              enableStickyHorizontalScroll
              onRefresh={() => refreshDataGrid(true)}
              exportOptions={{
                filename: 'reporte_conceptos_facturas',
                includeHeader: true,
                includeFooter: true,
                includeSummary: true
              }}
              // sx={{ height: 500 }}
              // Newest lots first, every time the report is opened.
              //
              // Via `defaultSort` and not `initialState.sorting`: the grid's sortModel is a
              // controlled prop, so MUI ignores initialState for it. The old
              // `sorting: [{ field: 'FECHA', sort: 'desc' }]` that used to sit below did
              // nothing at all -- what people actually saw was whatever order their browser
              // had saved from the last time they clicked a header, which is why the default
              // looked arbitrary.
              //
              // `Lote` needs no `type: 'number'`: it is an int in the database and arrives
              // as a JS number, so the default comparator already orders 316 above 99.
              defaultSort={ORDEN_POR_DEFECTO}
              forceDefaultSortOnMount
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                columns: {
                  columnVisibilityModel: {
                    ID: false, // Hide the ID column by default
                  },
                },
              }}
              columnVisibilityModel={{
                ID: false, // Hide the ID column by default
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              density="compact"
              filterMode="client"
              sortingMode="client"
              paginationMode="client"
              // Row selection is Community. Cell selection -- dragging a rectangle and pasting
              // over it, the spreadsheet gesture -- is NOT: `cellSelection` is declared in
              // MUI's `DataGridPremiumSharedPropsWithDefaultValue` and has no implementation in
              // the Community package. Verified against node_modules rather than the pricing
              // page. Selecting rows and applying one concept to them reaches the same outcome
              // without the licence.
              //
              // No `keepNonExistentRowsSelected` on purpose: row ids embed the array index, so
              // keeping a selection across a reload would re-attach it to other invoices.
              checkboxSelection
              rowSelectionModel={modeloSeleccion}
              onRowSelectionModelChange={setModeloSeleccion}
              // This table is wide enough that the checkbox scrolls out of view, which is
              // exactly when knowing which row you are ticking matters most.
              enableStickyCheckboxColumn
            />
          </Box>
        </CardContent>
      </Card>

      {/* Apply one concept to every selected invoice. Uses the same ConceptSelector as the
          pencil, so the list a person picks from is identical in both places -- and its catalog
          is a module-level cache, so it opens already populated. */}
      <Dialog
        open={dialogoLoteAbierto}
        onClose={() => !guardandoLote && setDialogoLoteAbierto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar concepto de aprobación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se aplicará a <strong>{facturasSeleccionadas.length} factura
            {facturasSeleccionadas.length === 1 ? '' : 's'}</strong> seleccionada
            {facturasSeleccionadas.length === 1 ? '' : 's'}.
          </Typography>
          <ConceptSelector
            value={conceptoLote}
            onChange={setConceptoLote}
            disabled={guardandoLote}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoLoteAbierto(false)} disabled={guardandoLote}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={aplicarConceptoALote}
            disabled={!conceptoLote || guardandoLote}
            startIcon={guardandoLote ? <CircularProgress size={16} /> : undefined}
          >
            {guardandoLote ? 'Aplicando…' : `Aplicar a ${facturasSeleccionadas.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MAFP Excel Download Modal */}
      <Dialog open={mafpModalOpen} onClose={() => setMafpModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Download color="primary" />
            <Typography variant="h6">Excel para MAFP</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Ingrese los números de lote a excluir del reporte, separados por comas.
            Deje en blanco para incluir todos los lotes.
          </Typography>
          <TextField
            label="Lotes a excluir"
            placeholder="Ej: 10,12,15"
            value={lotesExcluidos}
            onChange={(e) => setLotesExcluidos(e.target.value)}
            fullWidth
            size="small"
            helperText="Ejemplo: 10,12 o 15,16,17"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMafpModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<Download />} onClick={handleConfirmMAFPDownload}>
            Descargar reporte
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Invoice Modal */}
      <SplitInvoiceModal
        open={splitModalOpen}
        onClose={handleCloseSplitModal}
        originalRecord={selectedRecordForSplit}
        onConfirm={handleConfirmSplit}
        modalState={modalState}
        onModalStateChange={handleModalStateChange}
      />

      {/* Project Selector Dialog */}
      <Dialog
        open={showProjectSelector}
        onClose={() => setShowProjectSelector(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FolderOpen color="primary" />
            <Typography variant="h6">
              Seleccionar Proyecto
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <CostReportProjectSelector
              value={selectedProject}
              onChange={handleProjectChange}
              label="Proyecto"
              placeholder="Seleccionar proyecto para el reporte..."
              showActiveOnly={false}
              showStatusBadges={true}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Solo se muestran proyectos que tienen datos de costos. Los proyectos inactivos se muestran con indicadores visuales pero siguen siendo seleccionables para consultar reportes históricos.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProjectSelector(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReporteFacturasResult;